import { useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { CompanyItem, CourseForm, CuratorItem } from '../model';
import { CompanyAccessFields, StatusSelect } from './company-access-fields';

type Props = {
  open: boolean;
  form: CourseForm;
  companies: CompanyItem[];
  curators: CuratorItem[];
  curatorsLoadError?: string | null;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: () => void;
  onChange: (form: CourseForm) => void;
};

const curatorLabel = (curator: CuratorItem) =>
  [curator.name, curator.email].filter(Boolean).join(' — ') || curator.id;

export const CourseFormDialog = ({
  open,
  form,
  companies,
  curators,
  curatorsLoadError = null,
  isSaving,
  error,
  onClose,
  onSave,
  onChange,
}: Props) => {
  const curatorOptions = useMemo(() => {
    const map = new Map<string, CuratorItem>();
    curators.forEach((item) => map.set(item.id, item));
    form.curatorIds.forEach((id) => {
      if (!map.has(id)) {
        map.set(id, {
          id,
          name: `Куратор (id: ${id.slice(0, 8)}…)`,
        });
      }
    });
    return Array.from(map.values());
  }, [curators, form.curatorIds]);

  const selectedCurators = useMemo(
    () =>
      form.curatorIds
        .map((id) => curatorOptions.find((item) => item.id === id))
        .filter((item): item is CuratorItem => Boolean(item)),
    [curatorOptions, form.curatorIds],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{form.id ? 'Редактирование курса' : 'Создание курса'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {curatorsLoadError && (
            <Alert severity="warning">
              {curatorsLoadError}. Назначения кураторов не будут изменены при сохранении.
            </Alert>
          )}

          <TextField
            label="Название курса"
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Описание"
            value={form.description}
            onChange={(e) => onChange({ ...form, description: e.target.value })}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Аудитория"
            placeholder="Например: junior, middle, managers"
            value={form.audience}
            onChange={(e) => onChange({ ...form, audience: e.target.value })}
            fullWidth
          />
          <StatusSelect
            value={form.status}
            onChange={(status) => onChange({ ...form, status })}
          />
          <CompanyAccessFields
            form={form}
            companies={companies}
            onChange={(accessFields) => onChange({ ...form, ...accessFields })}
          />
          <Autocomplete
            multiple
            options={curatorOptions}
            value={selectedCurators}
            getOptionLabel={curatorLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={Boolean(curatorsLoadError)}
            onChange={(_, value) =>
              onChange({ ...form, curatorIds: value.map((item) => item.id) })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Кураторы курса"
                placeholder={curatorsLoadError ? 'Список недоступен' : 'Выберите кураторов'}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Отмена
        </Button>
        <Button variant="contained" onClick={onSave} disabled={isSaving}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};
